import PaginationControls from "../PaginationControls";

type PaginationPlacement = "top" | "bottom" | "both";

export type DashboardTableApiProps<T> = {
    data: T[],
    headings: string[],
    renderRowCells: (rowData: T) => React.ReactNode,
    itemName?: string,
    paginationPlacement?: PaginationPlacement,
    isLoading?: boolean,
    loadingRowCount?: number,
    pageIndex: number,
    pageSize: number,
    totalCount: number,
    totalPages: number,
    hasPreviousPage: boolean,
    hasNextPage: boolean,
    setPageIndex: (pageIndex: number) => void
}

const DashboardTableApi = <T extends { id: string }>({
    data,
    headings,
    renderRowCells,
    itemName = "items",
    paginationPlacement = "bottom",
    isLoading = false,
    loadingRowCount = 10,
    pageIndex,
    pageSize,
    totalCount,
    totalPages,
    hasPreviousPage,
    hasNextPage,
    setPageIndex,
}: DashboardTableApiProps<T>) => {
    const renderPagination = () => {
        if (data.length === 0 && !isLoading) return null;
        
        return (
            <PaginationControls
                pageIndex={pageIndex}
                pageSize={pageSize}
                totalCount={totalCount}
                totalPages={totalPages}
                hasPreviousPage={hasPreviousPage}
                hasNextPage={hasNextPage}
                setPageIndex={setPageIndex}
                itemName={itemName}
            />
        );
    };

    const renderTableContent = () => {
        if (isLoading) {
            return (
                <tbody>
                    {Array.from({ length: loadingRowCount }).map((_, rowIndex) => (
                        <tr key={`skeleton-${rowIndex}`}
                            className={`
                                ${rowIndex < loadingRowCount - 1 ? "border-b border-surface" : ""}
                            `}
                        >
                            {headings.map((_, colIndex) => (
                                <td 
                                    key={`skeleton-${rowIndex}-${colIndex}`} 
                                    className="px-4 sm:px-6 py-4"
                                >
                                    <div className="h-4 bg-chip-secondary rounded animate-pulse" />
                                </td>
                            ))}
                        </tr>
                    ))}
                </tbody>
            );
        }

        return (
            <tbody>
                {data.map((rowData, index) => (
                    <tr key={rowData.id} 
                        className={`
                            child:px-4 sm:child:px-6 child:py-2 sm:child:py-4 
                            ${index < data.length - 1 ? "border-b border-surface" : ""} 
                            text-[0.875rem] break-words
                        `}
                    >
                        {renderRowCells(rowData)}
                    </tr>
                ))}
            </tbody>
        );
    };

    return (
        <div className="rounded-lg border border-surface overflow-hidden">
            {/* Top Pagination */}
            {(paginationPlacement === "top" || paginationPlacement === "both") && (
                <div className="border-b border-surface">
                    {renderPagination()}
                </div>
            )}

            {/* Table Content */}
            <div className="overflow-x-auto">
                <table className="w-full text-left">
                    <thead className="bg-secondary text-white border-b-[3px] border-b-primary text-[0.875rem] whitespace-nowrap">
                        <tr className="child:px-4 sm:child:px-6 child:py-2 sm:child:py-4">
                            {headings.map(heading => (
                                <th key={heading}>
                                    {isLoading ? (
                                        <div className="h-4 bg-white/20 rounded animate-pulse" />
                                    ) : (
                                        heading
                                    )}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    {renderTableContent()}
                </table>
            </div>

            {/* Bottom Pagination */}
            {(paginationPlacement === "bottom" || paginationPlacement === "both") && renderPagination()}
        </div>
    );
};

export default DashboardTableApi;